import { UseFormGetValues, UseFormReturn } from "react-hook-form";

import { logger } from "c7-logger";

import { usePaymentOptionContext } from "checkout-experience/components/payment-method/payment-option-provider";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import {
  ChexFieldNames,
  ChexFieldValues,
  ChexNestedFormValue,
} from "checkout-query-client/types/chex-form-fields";
import { PaymentType } from "checkout-query-client/types/form-types";
import { isAltPaymentType } from "checkout-query-client/utils/helpers";

import { getIsFeatureEnabled } from "splitio-loader/src/utils/get-split-io-flag";

const isPazeReady = ({
  paymentType,
  getValues,
}: {
  paymentType?: PaymentType;
  getValues: UseFormGetValues<ChexNestedFormValue>;
}): boolean => {
  // If the payment method of Paze is selected then Error if there is no token
  // This is to prevent the user from clicking on the submit button before Paze has loaded
  if (paymentType === PaymentType.paze) {
    if (!getValues("pazePaymentMethod.pazeCompleteToken")) {
      logger.error("Pay Paze called without a token complete set");
      return false;
    }
  }
  return true;
};

export const useFormValidation = (checkoutForm: UseFormReturn) => {
  const { experienceType, target } = useChexContext();
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { formId = "", paymentType } = paymentOption ?? {};
  const {
    trigger,
    formState: { errors },
    getValues,
    setFocus,
  } = checkoutForm;

  return async (checkoutFormOverride?: UseFormReturn) => {
    let finalGetValues = getValues;
    let finalErrors = errors;
    let finalTrigger = trigger;
    let finalSetFocus = setFocus;

    // This occurs if someone clicks pay before a MOP is displayed/set
    if (!paymentType || paymentType === ("notSet" as PaymentType)) {
      logger.error("Pay called without a payment type set");
      return false;
    }

    // Verify if paze is ready
    const canSubmitPaze = isPazeReady({ paymentType, getValues });

    // Alt/Lpm payment types don't have forms
    if (isAltPaymentType(paymentType)) {
      return true;
    }

    if (checkoutFormOverride) {
      ({
        getValues: finalGetValues,
        formState: { errors: finalErrors },
        trigger: finalTrigger,
        setFocus: finalSetFocus,
      } = checkoutFormOverride);
    }

    if (experienceType === "dropInUI") {
      const values = finalGetValues(`${formId}`) as ChexFieldValues;
      const triggerValues = getNonFieldFrameFields(values, `${formId}`, target);

      const formFieldErrors = Object.keys(finalErrors[formId] ?? {}) ?? [];
      const isValid = await finalTrigger(triggerValues, { shouldFocus: true });

      const firstError =
        values && Object.keys(values).length > 0
          ? Object.keys(values).find((key) => formFieldErrors.includes(key))
          : undefined;

      /**
       * The second condition in "if" is to take care of "expiration" field.
       * If card is expired, then we are setting the error in RHF but
       * the form is not loaded, so getValues will not have expiration registered
       */
      if (
        formFieldErrors.filter((e) =>
          fieldFrameFields.includes(e as ChexFieldNames)
        ).length > 0 ||
        formFieldErrors.filter(
          (e) =>
            !triggerValues.includes(e) &&
            !fieldFrameFields.includes(e as ChexFieldNames)
        ).length > 0
      ) {
        if (firstError) finalSetFocus(`${formId}.${firstError}`);
        return false;
      }
      if (!isValid && firstError) finalSetFocus(`${formId}.${firstError}`);
      return canSubmitPaze && isValid;
    } else {
      const isValid = await finalTrigger(`${formId}`, { shouldFocus: true });
      return canSubmitPaze && isValid;
    }
  };
};

const getNonFieldFrameFields = (
  values: ChexFieldValues,
  formId: string,
  target?: string
) => {
  const isExpiryPciEnabled = getIsFeatureEnabled(target, "q1-2025-pci-expiry");
  const frameFieldsCombined = isExpiryPciEnabled
    ? [...fieldFrameFields, ...expiryFrameFields]
    : fieldFrameFields;
  const keys = values ? Object.keys(values) : [];
  return keys
    .filter((key) => !frameFieldsCombined.includes(key as ChexFieldNames))
    .map((v) => `${formId}.${v}`);
};

const fieldFrameFields: Array<ChexFieldNames> = [
  "cardNumber",
  "accountNumber",
  "confirmAccountNumber",
  "bic",
  "iban",
  "securityCode",
  "nameOnCard",
  "giftCardNumber",
];

const expiryFrameFields: Array<ChexFieldNames> = [
  "expiration",
  "expirationYear",
  "expirationMonth",
];
